import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { MorphSelect, type MorphSelectOption } from '@/components/ui/MorphSelect'
import { Pagination } from '@/components/ui/Pagination'
import { morphToast } from '@/components/ui/Toast'
import { useProjectMembersStore } from '@/stores/projectMembersStore'
import { useProjectCan } from '@/hooks/useProjectCan'
import { ProjectRoleValue, type ProjectRole } from '@/api/types'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { memberApi } from '@/api/memberApi'

interface ManageAccessDialogProps {
  open: boolean
  onClose: () => void
  projectId: number
  projectName: string
  workspaceId: number
}

// ── Project role string helpers ──────────────────────────────────────────────

type ProjectRoleStr = 'OWNER' | 'ADMIN' | 'EDITOR' | 'VIEWER'

const PROJECT_ROLE_STR: Record<ProjectRole, ProjectRoleStr> = {
  1: 'OWNER',
  2: 'ADMIN',
  3: 'EDITOR',
  4: 'VIEWER',
}

const PROJECT_ROLE_NUM: Record<string, ProjectRole> = {
  OWNER: ProjectRoleValue.OWNER,
  ADMIN: ProjectRoleValue.ADMIN,
  EDITOR: ProjectRoleValue.EDITOR,
  VIEWER: ProjectRoleValue.VIEWER,
}

const PROJECT_ROLE_OPTIONS: MorphSelectOption<ProjectRoleStr>[] = [
  { value: 'ADMIN', label: 'Admin', description: 'Gerencia membros e configurações do projeto.' },
  { value: 'EDITOR', label: 'Edição', description: 'Pode editar diagramas e colaborar no projeto.' },
  { value: 'VIEWER', label: 'Leitura', description: 'Acesso somente leitura aos diagramas.' },
]

const PROJECT_ROLE_FILTER_OPTIONS: MorphSelectOption<ProjectRoleStr>[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'EDITOR', label: 'Edição' },
  { value: 'VIEWER', label: 'Leitura' },
]

const PAGE_SIZE = 5

// ────────────────────────────────────────────────────────────────────────────

// ── Add Member Dialog ─────────────────────────────────────────────────────────

interface AddMemberDialogProps {
  open: boolean
  onClose: () => void
  onAdd: (email: string, role: ProjectRoleStr) => Promise<void>
  loadWorkspaceMembers: (query: string) => Promise<MorphSelectOption<string>[]>
}

function AddMemberDialog({ open, onClose, onAdd, loadWorkspaceMembers }: AddMemberDialogProps) {
  const { t } = useTranslation()
  const [addEmail, setAddEmail] = useState<string | null>(null)
  const [addRole, setAddRole] = useState<ProjectRoleStr>('EDITOR')
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    if (open) {
      setAddEmail(null)
      setAddRole('EDITOR')
    }
  }, [open])

  async function handleSubmit() {
    if (!addEmail) return
    setAdding(true)
    try {
      await onAdd(addEmail, addRole)
      onClose()
    } finally {
      setAdding(false)
    }
  }

  return (
    <Dialog
      open={open}
      title={t('manageAccess.addMember', 'Adicionar membro')}
      onClose={onClose}
      onConfirm={() => void handleSubmit()}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={adding}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={adding || !addEmail}>
            {adding ? t('common.loading', 'Aguarde...') : t('manageAccess.addButton', 'Adicionar')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 340 }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
            {t('manageAccess.memberLabel', 'Membro')}
          </div>
          <MorphSelect<string>
            value={addEmail}
            onChange={(v) => setAddEmail(v)}
            loadOptions={loadWorkspaceMembers}
            searchable
            clearable
            disabled={adding}
            placeholder={t('manageAccess.addPlaceholder', 'Buscar membro do workspace...')}
            searchPlaceholder={t('manageAccess.addPlaceholder', 'Buscar por nome ou email...')}
            emptyMessage={t('manageAccess.noWorkspaceMembers', 'Nenhum membro disponível')}
            debounceMs={250}
            size="md"
            minMenuWidth={300}
          />
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 6 }}>
            {t('manageAccess.roleLabel', 'Função')}
          </div>
          <MorphSelect<ProjectRoleStr>
            value={addRole}
            onChange={(v) => { if (v) setAddRole(v) }}
            options={PROJECT_ROLE_OPTIONS}
            disabled={adding}
            size="md"
            ariaLabel={t('manageAccess.roleLabel', 'Função')}
          />
        </div>
      </div>
    </Dialog>
  )
}

// ── Main Dialog ───────────────────────────────────────────────────────────────

export function ManageAccessDialog({
  open,
  onClose,
  projectId,
  projectName,
  workspaceId,
}: ManageAccessDialogProps) {
  const { t } = useTranslation()
  const members = useProjectMembersStore((s) => s.members)
  const loading = useProjectMembersStore((s) => s.loading)
  const load = useProjectMembersStore((s) => s.load)
  const addMember = useProjectMembersStore((s) => s.add)
  const updateMember = useProjectMembersStore((s) => s.update)
  const removeMember = useProjectMembersStore((s) => s.remove)
  const makeOwnerAction = useProjectMembersStore((s) => s.makeOwner)

  const canManage = useProjectCan('manage_members')
  const canTransfer = useProjectCan('transfer_owner')

  const [addDialogOpen, setAddDialogOpen] = useState(false)

  const [filterQuery, setFilterQuery] = useState('')
  const [filterRole, setFilterRole] = useState<ProjectRoleStr | null>(null)
  const [memberPage, setMemberPage] = useState(1)

  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null)
  const [confirmOwnerId, setConfirmOwnerId] = useState<number | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)

  useEffect(() => {
    if (open) {
      void load(projectId)
      setAddDialogOpen(false)
      setFilterQuery('')
      setFilterRole(null)
      setMemberPage(1)
      setConfirmRemoveId(null)
      setConfirmOwnerId(null)
    }
  }, [open, projectId, load])

  useEffect(() => { setMemberPage(1) }, [filterQuery, filterRole])

  const filteredMembers = useMemo(() => {
    const q = filterQuery.trim().toLowerCase()
    return members.filter((m) => {
      if (filterRole && PROJECT_ROLE_STR[m.role as ProjectRole] !== filterRole) return false
      if (!q) return true
      return m.user.name?.toLowerCase().includes(q) || m.user.email.toLowerCase().includes(q)
    })
  }, [members, filterQuery, filterRole])

  const totalMemberPages = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE))
  const pagedMembers = filteredMembers.slice((memberPage - 1) * PAGE_SIZE, memberPage * PAGE_SIZE)

  const existingUserEmails = new Set(members.map((m) => m.user.email))

  const loadWorkspaceMembers = useCallback(
    async (query: string): Promise<MorphSelectOption<string>[]> => {
      const page = await memberApi.list(workspaceId, { q: query, is_active: true, limit: 20 })
      return page.results
        .filter((m) => !existingUserEmails.has(m.user.email))
        .map((m) => ({
          value: m.user.email,
          label: m.user.name || m.user.email,
          description: m.user.name ? m.user.email : undefined,
          metadata: { userId: m.user.id },
          icon: <UserAvatar userId={m.user.id} name={m.user.name} email={m.user.email} size={22} />,
        }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceId, members],
  )

  async function handleAdd(email: string, role: ProjectRoleStr) {
    await addMember(projectId, { email, role: PROJECT_ROLE_NUM[role] })
    morphToast.success(t('manageAccess.addMember'))
  }

  async function handleRoleChange(memberId: number, roleStr: ProjectRoleStr) {
    setSavingId(memberId)
    try {
      await updateMember(projectId, memberId, { role: PROJECT_ROLE_NUM[roleStr] })
    } catch (err) {
      morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setSavingId(null)
    }
  }

  async function handleRemove(memberId: number) {
    setSavingId(memberId)
    try {
      await removeMember(projectId, memberId)
      morphToast.success(t('members.removed', 'Membro removido.'))
    } catch (err) {
      morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setSavingId(null)
      setConfirmRemoveId(null)
    }
  }

  async function handleMakeOwner(memberId: number) {
    setSavingId(memberId)
    try {
      await makeOwnerAction(projectId, memberId)
      morphToast.success(t('manageAccess.makeOwner'))
    } catch (err) {
      morphToast.fromError(err, t('common.error', 'Erro'))
    } finally {
      setSavingId(null)
      setConfirmOwnerId(null)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        title={`${t('manageAccess.title', 'Gerenciar acesso')} — ${projectName}`}
        onClose={onClose}
        size="lg"
        maxWidth={800}
        footer={
          <Button variant="ghost" onClick={onClose}>
            {t('common.close', 'Fechar')}
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* ── Toolbar: filter + add button ────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              placeholder={t('manageAccess.filterPlaceholder', 'Filtrar por nome ou email...')}
              className="morph-input"
              style={{ flex: 1 }}
            />
            <div style={{ flexShrink: 0, width: 190 }}>
              <MorphSelect<ProjectRoleStr>
                value={filterRole}
                onChange={(v) => setFilterRole(v)}
                options={PROJECT_ROLE_FILTER_OPTIONS}
                clearable
                placeholder={t('manageAccess.allRoles', 'Todas as funções')}
                size="md"
                ariaLabel={t('manageAccess.roleFilter', 'Filtrar por função')}
              />
            </div>
            {canManage && (
              <Button size="sm" onClick={() => setAddDialogOpen(true)}>
                {t('manageAccess.addButton', '+ Adicionar membro')}
              </Button>
            )}
          </div>

          {/* ── Members list ────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {loading ? (
              <div style={{ fontSize: 12, opacity: 0.5, padding: '8px 0' }}>
                {t('manageAccess.loading', 'Carregando...')}
              </div>
            ) : members.length === 0 ? (
              <div style={{ fontSize: 12, opacity: 0.5, padding: '8px 0' }}>
                {t('manageAccess.noMembers', 'Nenhum membro no projeto.')}
              </div>
            ) : filteredMembers.length === 0 ? (
              <div style={{ fontSize: 12, opacity: 0.5, padding: '8px 0' }}>
                {t('manageAccess.noMatch', 'Nenhum membro corresponde à busca.')}
              </div>
            ) : (
              pagedMembers.map((m) => {
                const isOwner = m.role === ProjectRoleValue.OWNER
                const isConfirmingRemove = confirmRemoveId === m.id
                const isConfirmingOwner = confirmOwnerId === m.id
                const isSaving = savingId === m.id
                const roleStr = PROJECT_ROLE_STR[m.role as ProjectRole]

                return (
                  <div
                    key={m.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 4px',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <UserAvatar userId={m.user.id} name={m.user.name} email={m.user.email} size={30} />

                    {/* Name/email */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.user.name || m.user.email}
                      </div>
                      <div style={{ fontSize: 10, opacity: 0.55 }}>{m.user.email}</div>
                    </div>

                    {/* Role — owner badge or editable MorphSelect */}
                    {isOwner ? (
                      <span
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 4,
                          background: 'var(--accent-muted)',
                          color: 'var(--accent)',
                          border: '1px solid var(--accent-border)',
                          flexShrink: 0,
                        }}
                      >
                        {t('projectRole.owner', 'Owner')}
                      </span>
                    ) : canManage ? (
                      <div style={{ flexShrink: 0, width: 190 }}>
                        <MorphSelect<ProjectRoleStr>
                          value={roleStr}
                          onChange={(v) => {
                            if (v && v !== roleStr) void handleRoleChange(m.id, v)
                          }}
                          options={PROJECT_ROLE_OPTIONS}
                          disabled={isSaving}
                          size="sm"
                          ariaLabel={t('manageAccess.roleLabel', 'Função')}
                        />
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, opacity: 0.7, flexShrink: 0 }}>
                        {t(`projectRole.${roleStr.toLowerCase()}`)}
                      </span>
                    )}

                    {/* Actions */}
                    {canManage && !isOwner && (
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        {canTransfer && (
                          isConfirmingOwner ? (
                            <button
                              onClick={() => void handleMakeOwner(m.id)}
                              disabled={isSaving}
                              title={t('manageAccess.makeOwnerConfirm', 'Confirmar transferência')}
                              style={confirmBtnStyle('warning')}
                            >
                              ✓
                            </button>
                          ) : (
                            <button
                              onClick={() => { setConfirmOwnerId(m.id); setConfirmRemoveId(null) }}
                              disabled={isSaving}
                              title={t('manageAccess.makeOwner', 'Tornar owner')}
                              style={actionBtnStyle}
                            >
                              ★
                            </button>
                          )
                        )}
                        {isConfirmingRemove ? (
                          <button
                            onClick={() => void handleRemove(m.id)}
                            disabled={isSaving}
                            title={t('manageAccess.removeConfirm', 'Confirmar remoção')}
                            style={confirmBtnStyle('danger')}
                          >
                            ✓
                          </button>
                        ) : (
                          <button
                            onClick={() => { setConfirmRemoveId(m.id); setConfirmOwnerId(null) }}
                            disabled={isSaving}
                            title={t('manageAccess.removeMember', 'Remover membro')}
                            style={removeBtnStyle}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    )}
                    {canManage && isOwner && (
                      <div style={{ width: 4, flexShrink: 0 }} />
                    )}
                  </div>
                )
              })
            )}
          </div>

          {totalMemberPages > 1 && (
            <Pagination page={memberPage} totalPages={totalMemberPages} onChange={setMemberPage} />
          )}
        </div>
      </Dialog>

      <AddMemberDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAdd}
        loadWorkspaceMembers={loadWorkspaceMembers}
      />
    </>
  )
}

const actionBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  borderRadius: 4,
  padding: '2px 6px',
  fontSize: 11,
  cursor: 'pointer',
}

const removeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--color-danger-border)',
  color: 'var(--color-danger)',
  borderRadius: 4,
  padding: '2px 6px',
  fontSize: 11,
  cursor: 'pointer',
}

function confirmBtnStyle(variant: 'danger' | 'warning'): React.CSSProperties {
  return {
    background: variant === 'danger' ? 'var(--color-danger)' : 'var(--color-warning)',
    border: 'none',
    color: '#fff',
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 11,
    cursor: 'pointer',
    fontWeight: 600,
  }
}
